import {
    createContext,
    ReactNode,
    useCallback,
    useEffect,
    useReducer,
  } from "react";
  
  import {
    AuthenticationDetails,
    CognitoUser,
    CognitoUserAttribute,
    CognitoUserPool,
    CognitoUserSession,
    
  } from "amazon-cognito-identity-js";

  import { Auth } from 'aws-amplify'

  import {
    ActionMap,
    AuthState,
    AuthUser,
    CognitoContextType,
  } from "../types/auth";
  
  import axios from "../utils/axios";
  import { cognitoConfig, cognitoSchool, cognitoSchoolCode } from "../config";
import { CognitoAttribute } from "utils/types";
  
  const INITIALIZE = "INITIALIZE";
  const UPDATE = "UPDATE";
  const SIGN_OUT = "SIGN_OUT";
  
  const UserPool = new CognitoUserPool({
    UserPoolId: cognitoConfig.userPoolId || "",
    ClientId: cognitoConfig.clientId || "",
  });
  
  const initialState: AuthState = {
    isAuthenticated: false,
    isInitialized: false,
    user: null,
  };
  
  type AuthActionTypes = {
    [INITIALIZE]: {
      isAuthenticated: boolean;
      user: AuthUser;
    };
    [UPDATE]: {
      isAuthenticated: boolean;
      user: AuthUser;
    };
    [SIGN_OUT]: undefined;
  };

  
  
  type CognitoActions =
    ActionMap<AuthActionTypes>[keyof ActionMap<AuthActionTypes>];
  
  const reducer = (state: AuthState, action: CognitoActions) => {
    console.log("Redsucer :",action.type)
    if (action.type === INITIALIZE) {
      const { isAuthenticated, user } = action.payload;
      console.log("Reducer Authorised :",user)
      return {
        ...state,
        isAuthenticated,
        isInitialized: true,
        user,
      };
    }
    if (action.type === UPDATE) {
      const { isAuthenticated, user } = action.payload;
      console.log("Reducer Update :",user)
      return {
        ...state,
        isAuthenticated,
        user,
      };
    }
    if (action.type === SIGN_OUT) {
      console.log("Reducer: Sign out")
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };
    }
    return state;
  };
  
  const AuthContext = createContext<CognitoContextType | null>(null);
  
  function AuthProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(reducer, initialState);
  
    const getUserAttributes = useCallback(
      (currentUser: CognitoUser): Record<string, any> =>
        new Promise((resolve, reject) => {
          currentUser.getUserAttributes((err, attributes) => {
            if (err) {
              reject(err);
            } else {
              const results: Record<string, any> = {};
  
              attributes?.forEach((attribute) => {
                results[attribute.Name] = attribute.Value;
              });
              resolve(results);
            }
          });
        }),
      []
    );

    const updateUserAttributes = useCallback(
      (currentUser: CognitoUser, attributes: CognitoAttribute[]): Record<string, any> =>
        new Promise((resolve, reject) => {
          currentUser.updateAttributes(attributes, (err, result) => {
            if (err) {
              reject(err);
            } else {
              console.log('New update attribute result: ' + result);
              resolve(result);
            }
          });
        }),
      []
    );

  
    const getSession = useCallback(
      () =>
        new Promise((resolve, reject) => {
          const user = UserPool.getCurrentUser();
          if (user) {
            user.getSession(
              async (err: Error | null, session: CognitoUserSession | null) => {
                if (err) {
                  reject(err);
                } else {
                  console.log("Getting attributes")
                  const attributes = await getUserAttributes(user);
                  console.log("Got attributes", attributes)
                  const token = session?.getIdToken().getJwtToken();
                  console.log("Got tokens!")
                  axios.defaults.headers.common.Authorization = token!;
  
                  dispatch({
                    type: INITIALIZE,
                    payload: { isAuthenticated: true, user: attributes },
                  });
  
                  resolve({
                    user,
                    session,
                    headers: { Authorization: token },
                  });
                }
              }
            );
          } else {
            dispatch({
              type: INITIALIZE,
              payload: {
                isAuthenticated: false,
                user: null,
              },
            });
          }
        }),
      [getUserAttributes]
    );
  
    const initialize = useCallback(async () => {
      try {
        await getSession();
        console.log("Initialised",)
      } catch {
        dispatch({
          type: INITIALIZE,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    }, [getSession]);
  
    useEffect(() => {
      initialize();
    }, [initialize]);
  
    const signIn = useCallback(
      (email: any, password: any) =>
        new Promise((resolve, reject) => {
          const user = new CognitoUser({
            Username: email,
            Pool: UserPool,
          });
  
          const authDetails = new AuthenticationDetails({
            Username: email,
            Password: password,
          });
          console.log("Auth: ",authDetails)
          user.authenticateUser(authDetails, {
            onSuccess: async (data) => {
              await getSession();
              console.log("Signed in")
              resolve(data);
            },
            onFailure: (err) => {
              console.log("NOT logged in")
              reject(err);
            },
            newPasswordRequired: () => {
              resolve({ message: "New password required" });
            },
          });
        }),
      [getSession]
    );
  
    const signOut = () => {
      const user = UserPool.getCurrentUser();
      console.log("Sign out - user :",user)
      if (user) {
        user.signOut();
        console.log("Signed out")
        dispatch({ type: SIGN_OUT });
      }
    };
  
    //		Cognito:  signUp(
          // 	username: string,
          // 	password: string,
          // 	userAttributes: CognitoUserAttribute[],
          // 	validationData: CognitoUserAttribute[],
          // 	callback: NodeCallback<Error, ISignUpResult>,
          // 	clientMetadata?: ClientMetadata
          // ): void;
  
      // export interface ISignUpResult {
      //   user: CognitoUser;
      //   userConfirmed: boolean;
      //   userSub: string;
      //   codeDeliveryDetails: CodeDeliveryDetails;
      // }
  
      // export interface CodeDeliveryDetails {
      //   AttributeName: string;
      //   DeliveryMedium: string;
      //   Destination: string;
      // }

    const confirmSignUp = (
        email: string,
        password: string,
        code: string,
    ) => 
      new Promise((resolve, reject) => {
        var userData = {
            Username: email,
            Pool: UserPool,
        };
        var authenticationData = {
            Username: 'username',
            Password: 'password',
        };
        var authenticationDetails = new AuthenticationDetails(
            authenticationData
        );
        console.log("Confirm signUp ",email, code)
        
        var cognitoUser = new CognitoUser(userData);
        cognitoUser.confirmRegistration(code, false, 
            async (err, result) => {  // 
                if (err) {
                    console.log("In confirm with code Error: ",err)
                    reject(err);
                    return;
                } 
                console.log("In confirm with code: ",result)
                resolve(result);

                // Now login the user
            }
        )
      }
    );

    const resendConfirmationCode = (
        email: string,
    ) => 
      new Promise((resolve, reject) => {
        var userData = {
            Username: email,
            Pool: UserPool,
        };
        
        var cognitoUser = new CognitoUser(userData);
        cognitoUser.resendConfirmationCode(
            async (err, result) => {  // 
                if (err) {
                    console.log("In confirmCode Error: ",err)
                    reject(err);
                  return;
                } 
                console.log("In confirmCode: ",result)
                resolve(result);
                // Set destination URL here
                window.location.href = "";
            }
        )            
      });       

    const resetPassword = (
        email: string,
        oldPassword: string,
        newPassword: string,
    ) => 
      new Promise((resolve, reject) => {
        var userData = {
            Username: email,
            Pool: UserPool,
        };
        
        var cognitoUser = new CognitoUser(userData);
        cognitoUser.changePassword( oldPassword, newPassword, 
            async (err, result) => {  // 
                if (err) {
                    console.log("In changePassword Error: ",err)
                    reject(err);
                  return;
                } 
                console.log("In changePassword: ",result)
                resolve(result);
                // Set destination URL here
                //window.location.href = "";
            }
        )            
      });       

    const forgotPassword = (
        email: string,
    ) => 
        new Promise((resolve, reject) => {
        var userData = {
            Username: email,
            Pool: UserPool,
        };
        
        var cognitoUser = new CognitoUser(userData);
        cognitoUser.forgotPassword({
            
            onSuccess: function(data) {
                // successfully initiated reset password request
                console.log('CodeDeliveryData from forgotPassword: ' + data);
                resolve(data)
            },
            onFailure: function(err) {
                console.log(err.message || JSON.stringify(err));
                reject(err)
            },
            //Optional automatic callback
            // inputVerificationCode: function(data) {
            //     console.log('Code sent to: ' + data);
            //     var verificationCode = document.getElementById('code').value;
            //     var newPassword = document.getElementById('new_password').value;
            //     cognitoUser.confirmPassword(verificationCode, newPassword, {
            //         onSuccess() {
            //             console.log('Password confirmed!');
            //         },
            //         onFailure(err) {
            //             console.log('Password not confirmed!');
            //         },
            //     });
            // },
        }
        )            
      }); 
      
    const confirmNewPassword = (
        email: string,
        verificationCode: string,
        newPassword: string,
    ) => 
      new Promise((resolve, reject) => {
        var userData = {
            Username: email,
            Pool: UserPool,
        };
        
        var cognitoUser = new CognitoUser(userData);
        cognitoUser.changePassword( verificationCode, newPassword, 
            async (err, result) => {  // 
                if (err) {
                    console.log("In verify new Password Error: ",err)
                    reject(err);
                  return;
                } 
                console.log("In verify new Password: ",result)
                resolve(result);
                // Set destination URL here
                window.location.href = "";
            }
        )            
      });       

    const updateAttribute = async (
        email: string,
        attributes: CognitoAttribute[],
    ) => 
      new Promise((resolve, reject) => {
        // var attributeList: CognitoAttribute[] = [];
        // var userData = {
        //   Username: email,
        //   Pool: UserPool,
        // };
        // var updatedattribute = new CognitoUserAttribute(attribute);
        // attributeList.push(updatedattribute);

        const cognitoUser = UserPool.getCurrentUser();
        if (cognitoUser) {
          cognitoUser.getSession(
            async (err: Error | null, session: CognitoUserSession | null) => {
              if (err) {
                reject(err);
              } else {
                // const token = session?.getIdToken().getJwtToken();
                console.log("* Updating attributes")
                const result = await updateUserAttributes(cognitoUser, attributes) 
                console.log("* Updated attributes result ",result)
                const newattributes = await getUserAttributes(cognitoUser); 
                console.log("* New attributes :",newattributes)
                dispatch({
                  type: UPDATE,
                  payload: { isAuthenticated: true, user: newattributes },
                });
                resolve(newattributes)

                //   if (err) {
                //     alert(err.message || JSON.stringify(err));
                //     return;
                //   }
                //   console.log('update attribute result: ' + result);
          
                //   const newattributes = await getUserAttributes(cognitoUser);
                //   console.log("New attributes :",newattributes)
                //   dispatch({
                //     type: UPDATE,
                //     payload: { isAuthenticated: true, user: newattributes },
                //   });
                //   resolve(newattributes)
  
                // });

              }
            }
          );

        }
      });       


    const signUp = (
      email: string,
      password: string,
      firstName: string,
      lastName: string,
      prefix: string,
      userType: string = 'TEACHER',
    ) =>
      new Promise((resolve, reject) => {
        console.log("Sign Up", UserPool.getUserPoolId)
        UserPool.signUp(
          email,
          password,
          [
            new CognitoUserAttribute({ Name: "email", Value: email }),
            new CognitoUserAttribute({
                Name: "given_name",
                Value: `${firstName}`,
              }),
            new CognitoUserAttribute({
                Name: "family_name",
                Value: `${lastName}`,
            }),
            new CognitoUserAttribute({
                Name: "custom:prefix",
                Value: `${prefix}`,
            }),
            // if school info not provided here should default to the Esteam values
            // new CognitoUserAttribute({
            //     Name: "custom:school",
            //     Value: `${cognitoSchool}`,
            // }),
            // new CognitoUserAttribute({
            //     Name: "custom:school_code",
            //     Value: `${cognitoSchoolCode}`,
            // }),
            new CognitoUserAttribute({
                Name: "custom:user_type",
                Value: `${userType}`,
            }),
          ],
          [],
          async (err) => {  // 
            console.log("In signUp ",err)
            if (err) {
              reject(err);
              return;
            }
            resolve(undefined);
            // Set destination URL here
            //window.location.href = "";
          }
        );
      });
  
  
    return (
      <AuthContext.Provider
        value={{
          ...state,
          method: "cognito",
          user: {
            displayName: state?.user?.name || "Undefined",
            role: "user",
            ...state.user,
          },
          signIn,
          signUp,
          confirmSignUp,
          resendConfirmationCode,
          signOut,
          forgotPassword,
          resetPassword,
          confirmNewPassword,
          updateAttribute,
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  }
  
  export { AuthContext, AuthProvider };
  